'use client';

import { useEffect } from 'react';

const MEDIA_ORIGIN = 'https://media.officeportal.io';
const COOKIE_NAME = 'op_media_token';
const STORAGE_KEY = 'op_media_jwt';

/**
 * MediaAuthProvider
 *
 * Invisible client component that bridges JWT authentication to
 * Media Portal embed iframes on help pages.
 *
 * Token sources (checked in order):
 *  1. URL hash fragment:  /help/topic/article#token=JWT
 *  2. Cookie:             op_media_token (set by POST /api/help-auth)
 *  3. sessionStorage:     op_media_jwt (persists across help page navigations)
 *
 * Auth flow:
 *  - Media Portal iframe sends postMessage { type: 'media-auth-request' }
 *  - This component responds with { type: 'auth', jwt } to the iframe
 */
export function MediaAuthProvider() {
  useEffect(() => {
    // --- Resolve JWT from available sources ---
    let jwt = '';

    // 1. URL hash fragment: #token=eyJhbG...
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const hashToken = hashParams.get('token');
      if (hashToken) {
        jwt = hashToken;
        // Clean hash from URL
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    // 2. Cookie: op_media_token
    if (!jwt && typeof document !== 'undefined') {
      const match = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'),
      );
      if (match?.[1]) {
        jwt = decodeURIComponent(match[1]);
        // Clear cookie after reading (one-time use)
        document.cookie = `${COOKIE_NAME}=; path=/help; max-age=0`;
      }
    }

    // 3. sessionStorage
    if (!jwt && typeof sessionStorage !== 'undefined') {
      jwt = sessionStorage.getItem(STORAGE_KEY) || '';
    }

    // Persist to sessionStorage for subsequent navigations
    if (jwt) {
      try {
        sessionStorage.setItem(STORAGE_KEY, jwt);
      } catch {
        // storage full — ignore
      }
    }

    if (!jwt) return;

    // --- Listen for auth requests from Media Portal iframes ---
    function handleMessage(event: MessageEvent) {
      // Only respond to media-auth-request
      if (!event.data || event.data.type !== 'media-auth-request') return;

      // Only send JWT to our media portal origin, or if origin is unknown (some browsers)
      if (event.origin && event.origin !== 'null' && event.origin !== MEDIA_ORIGIN) return;

      const source = event.source as Window | null;
      if (source) {
        source.postMessage({ type: 'auth', jwt }, MEDIA_ORIGIN);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return null; // Invisible component
}
